"use client"

import { useContext, useEffect, useState, useMemo } from "react"
import { TypesenseContext } from "./TypesenseProvider"
import { getTypesenseKey, UserContext } from "./getTypesenseKey"

export interface UseAllFacetValuesProps {
  collectionName: string
  attribute: string
  context: UserContext
  enabled?: boolean
  limit?: number
}

export interface UseAllFacetValuesResult {
  values: string[]
  loading: boolean
}

/**
 * Fetches all unique facet values for an attribute from Typesense.
 * Independent of InstantSearch state - not affected by page filters.
 */
export const useAllFacetValues = ({
  collectionName,
  attribute,
  context,
  enabled = true,
  limit = 1000,
}: UseAllFacetValuesProps): UseAllFacetValuesResult => {
  const [values, setValues] = useState<string[]>([])
  const [loading, setLoading] = useState(false)

  const { typesenseHost, apiKey, multiCollectionKeysV2 } =
    useContext(TypesenseContext)

  const collectionApiKey = useMemo(
    () =>
      getTypesenseKey({
        multiCollectionKeysV2,
        searchApiKey: apiKey,
        collectionName,
        context,
        usePermissions: false,
      }),
    [multiCollectionKeysV2, apiKey, collectionName, context],
  )

  useEffect(() => {
    if (!enabled) {
      setValues([])
      return
    }

    if (!collectionApiKey || !typesenseHost) {
      return
    }

    const abortController = new AbortController()

    const fetchFacetValues = async () => {
      setLoading(true)

      try {
        const searchParams = new URLSearchParams({
          q: "*",
          query_by: attribute,
          facet_by: attribute,
          max_facet_values: limit.toString(),
          per_page: "0",
        })

        const response = await fetch(
          `${typesenseHost}/search/collections/${collectionName}/documents/search?${searchParams.toString()}`,
          {
            headers: { "X-Typesense-Api-Key": collectionApiKey },
            signal: abortController.signal,
          },
        )

        if (!response.ok) {
          throw new Error(`Typesense API error: ${response.status}`)
        }

        const data = await response.json()
        const facetCounts = data.facet_counts?.find(
          (f: { field_name: string }) => f.field_name === attribute,
        )

        if (facetCounts?.counts) {
          setValues(facetCounts.counts.map((c: { value: string }) => c.value))
        } else {
          setValues([])
        }
      } catch (error) {
        if ((error as Error).name === "AbortError") return
        console.error("Error fetching facet values:", error)
        setValues([])
      } finally {
        setLoading(false)
      }
    }

    fetchFacetValues()

    return () => abortController.abort()
  }, [
    enabled,
    collectionApiKey,
    typesenseHost,
    collectionName,
    attribute,
    limit,
  ])

  return { values, loading }
}
